import React, { useEffect, useState } from "react";
import { Button, Checkbox, CheckboxProps, Form, Input, message, Modal, Popconfirm, Spin } from "antd";
import { useCompany } from "../../contexts/CompanyContext";
import { useConfig } from "../../contexts/ConfigContext";
import { mutate } from "swr";
import { useCashRegister } from "../../contexts/CashRegisterContext"; // You need to export this hook from Context
import { usePos } from "../../contexts/PosContext";
import { useSecureRequest } from "../../hooks/useSecureRequest";
import { EditOutlined } from "@ant-design/icons";

interface IformValues {
  name: string;
  alias: string;
  is_active: boolean;
}

type TRequestData = {
  company: string | null,
  module: string,
  uidPos?: string, 
  uidCashRegister?: string
  cashRegister: Partial<IformValues> & { 
    pos_uid?: string;
    pos_name?: string; 
    pos_id?: number;
    storehouse_id?: number;
    id?: number | null;
  },
}

function CashRegisterModal(){
  const {cashRegister, updateCashRegister} = useCashRegister()
  const {company} = useCompany()
  const {pos} = usePos()
  const {config} = useConfig()
  const [form] = Form.useForm<IformValues>()
  const [loading, setLoading] = useState(false)
  const [messageApi, contextHolder] = message.useMessage();
  const [edit, setEdit] = useState(false)
  
  // Decide if we are editing based on if we have a current uid
  const currentData = !!cashRegister.current?.uid
  const { securePost } = useSecureRequest()

  useEffect(()=>{
    if(cashRegister.current?.uid){
      form.setFieldsValue({
        name: cashRegister.current.name,
        alias: cashRegister.current.alias,
        is_active: cashRegister.current.is_active
      })
    }else{
      // Default values for new register
      form.setFieldsValue({
        name: "",
        alias: "",
        is_active: true
      })
    }
  },[cashRegister.current, cashRegister.showModal])

  const handleRegister = async (values:IformValues) => {
    setLoading(true)
    // We assume the cash register is being added to the currently selected POS
    // If no POS selected, we might fail or require one. 
    // For now, let's assume valid POS is selected as per Session flow.
    const posUid = pos.currentSelected?.uid;

    if (!posUid && !currentData) {
        fail("Debe seleccionar un Punto de Venta");
        setLoading(false);
        return;
    }

    let dataRequest:TRequestData = {
      company: company.current.uid,
      module: config.module,
      cashRegister: {
        ...values,
        pos_id: pos.currentSelected?.id,
        storehouse_id: pos.currentSelected?.storehouse_id,
        pos_uid: posUid,
        pos_name: pos.currentSelected?.name 
      }
    }

    let url = "/sale/cash-register/create-cash-register"
  
    if(currentData){
      dataRequest.uidCashRegister = cashRegister.current.uid || ""
      dataRequest.cashRegister.id = cashRegister.current.id
      url = "/sale/cash-register/update-cash-register"
    }
    console.log("dataRequest",dataRequest)
    const storeResponse = await securePost(url, dataRequest)
    
    if(storeResponse.status === 200){
      // Revalidate the list
      mutate(key => Array.isArray(key) && typeof key[0] === 'string' && key[0].startsWith('/sale/cash-register/get-cash-register'), undefined, { revalidate: true })
      updateCashRegister({showModal: false, current: undefined})
      form.resetFields()
      setEdit(false)
      success(currentData ? 'Caja actualizada con éxito' : 'Caja creada con éxito')
    } else {
      fail("Por favor intente nuevamente")
    }
    setLoading(false)
  }

  const success = (content:string) => {
    messageApi.open({
      type: 'success',
      content,
    });
  };
  const fail = (content:string) => {
    messageApi.open({
      type: 'error',
      content,
    });
  };

  const onClose = () => {
    updateCashRegister({showModal: false, current: undefined})
    setEdit(false)
    form.resetFields()
  }
  const onChange: CheckboxProps['onChange'] = (e) => {
    setEdit(e.target.checked);
  };
  const footer = [
    <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      okButtonProps={{htmlType:"submit", form:"cash-register-form"}}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" disabled={!edit} htmlType="button">Actualizar</Button>
    </Popconfirm>,
    <Button 
      key="cancel" 
      type="default" 
      htmlType="button" 
      onClick={onClose}>Cancelar
    </Button>
  ]
  if(!currentData){
    footer[0] = <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      okButtonProps={{htmlType:"submit",form:"cash-register-form"}}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" htmlType="button">Crear</Button>
    </Popconfirm>
  }
  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{currentData ? 'Editar Caja' : 'Nueva Caja'}</div>}
      open={cashRegister.showModal}
      maskClosable={false}
      confirmLoading={loading}
      onCancel={onClose}
      destroyOnHidden
      footer={footer}
      >
      <Spin size="large" tip="Procesando..." fullscreen spinning={loading} />
      {contextHolder}
      <Form
        form={form}
        name="cash-register-form"
        layout="vertical"
        onFinish={handleRegister}>
       {currentData && <> <Checkbox checked={edit} onChange={onChange}>
          <EditOutlined style={{ marginRight: 6 }} />
          Editar
        </Checkbox>
        <Form.Item
          label="Nombre"
          name="name"
          rules={[{ required: true, message: 'Ingrese un nombre para la caja'}]}
        >
          <Input disabled={!edit} placeholder="Ej. Caja Principal" maxLength={50}/>
        </Form.Item>
        <Form.Item
          label="Identificador"
          name="alias"
          rules={[{ required: true, message: 'Ingrese un identificador para la caja'}]}
        >
          <Input disabled={!edit} placeholder="Ej. CAJ1" maxLength={50}/>
        </Form.Item>
        <Form.Item
          label="Estado"
          name="is_active"
          valuePropName="checked"
        >
          <Checkbox disabled={!edit}>Habilitado</Checkbox>
        </Form.Item></>}
       {!currentData && <> 
        <Form.Item
          label="Nombre"
          name="name"
          rules={[{ required: true, message: 'Ingrese un nombre para la caja'}]}
        >
          <Input placeholder="Ej. Caja Principal" maxLength={50}/>
        </Form.Item>
        <Form.Item
          label="Identificador"
          name="alias"
          rules={[{ required: true, message: 'Ingrese un identificador para la caja'}]}
        >
          <Input placeholder="Ej. CAJ1" maxLength={50}/>
        </Form.Item></>}
      </Form>
    </Modal>
  )

}

export default CashRegisterModal
